import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { reducedModeEnabled } from '../utils/reducedMode';

interface MembershipStatusProps {
  className?: string;
}

export const MembershipStatus: React.FC<MembershipStatusProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const subscription = useSubscription();

  const getTierColor = () => {
    switch (subscription.tier) {
      case 'party-pass': return 'bg-blue-500';
      case 'pro-subscription': return 'bg-purple-600';
      case 'free': return 'bg-gray-500';
    }
  };

  const getTierName = () => {
    switch (subscription.tier) {
      case 'party-pass': return 'Party Pass';
      case 'pro-subscription': return 'Pro';
      case 'free': return 'Free';
    }
  };

  const isExpired = subscription.expiresAt && subscription.expiresAt <= new Date();

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Membership Status</h3>
        <div className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getTierColor()}`}>
          {getTierName()}
        </div>
      </div>

      {subscription.timeRemaining && !isExpired && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-blue-600 text-sm font-medium">⏰ {subscription.timeRemaining} remaining</span>
          </div>
        </div>
      )}

      <div className="space-y-2 mb-4">
        <h4 className="font-medium text-gray-700">Current Features:</h4>
        <ul className="space-y-1">
          {subscription.features.map((feature, index) => (
            <li key={index} className="text-sm text-gray-600 flex items-center">
              <span className="text-green-500 mr-2">✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* Show upgrade button for free tier */}
      {subscription.tier === 'free' && (
        reducedModeEnabled ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            You are on the free hosting path. Feedback and feature requests stay available from inside the app.
          </div>
        ) : (
          <button
            onClick={() => navigate('/subscription')}
            className="w-full bg-qr-primary hover:bg-qr-secondary text-white py-2 rounded-lg font-medium transition-colors"
          >
            Upgrade Now
          </button>
        )
      )}

      {/* Show renewal for expired memberships */}
      {isExpired && subscription.tier !== 'free' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm font-medium">Membership Expired</p>
          <button
            onClick={() => navigate('/subscription')}
            className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors"
          >
            Renew Membership
          </button>
        </div>
      )}

      {/* Show manage button for active paid memberships */}
      {!isExpired && subscription.tier !== 'free' && !reducedModeEnabled && (
        subscription.tier === 'pro-subscription' ? (
          <button
            onClick={async () => {
              try {
                const functions = getFunctions(undefined, 'us-central1');
                const payment = httpsCallable(functions, 'payment');
                const result = await payment({ action: 'create-portal-session' });
                const { url } = (result.data as any);
                window.location.href = url;
              } catch (error: any) {
                console.error('Portal error:', error);
                alert(error?.message || 'Could not open subscription management.');
              }
            }}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg font-medium transition-colors"
          >
            Manage Subscription
          </button>
        ) : (
          <button
            onClick={() => navigate('/subscription')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg font-medium transition-colors"
          >
            View Plans
          </button>
        )
      )}
    </div>
  );
};